import React, { useState, useEffect } from 'react';
import { Package, Check, AlertTriangle, DollarSign, Clock } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { formatMoney } from '../../utils/formatters';
import './RecepcionModal.css';

const RecepcionModal = ({ orden, onRecibir, onClose, isLoading }) => {
    const [productosRecepcion, setProductosRecepcion] = useState([]);
    const [errors, setErrors] = useState({});
    const [estadoPago, setEstadoPago] = useState('pendiente'); // 'pendiente' o 'pagada'

    useEffect(() => {
        if (orden && orden.detalles) {
            setProductosRecepcion(
                orden.detalles.map(detalle => ({
                    detalle_id: detalle.id,
                    producto_id: detalle.producto_id,
                    producto_nombre: detalle.producto_nombre,
                    cantidad_ordenada: parseFloat(detalle.cantidad),
                    cantidad_recibida_anterior: parseFloat(detalle.cantidad_recibida || 0),
                    cantidad_pendiente: parseFloat(detalle.cantidad) - parseFloat(detalle.cantidad_recibida || 0),
                    cantidad_a_recibir: parseFloat(detalle.cantidad) - parseFloat(detalle.cantidad_recibida || 0),
                    precio_unitario: parseFloat(detalle.precio_unitario) || 0
                }))
            );
        }
    }, [orden]);

    const handleCantidadChange = (index, newCantidad) => {
        const cantidad = parseFloat(newCantidad);
        if (isNaN(cantidad) || cantidad < 0) return;

        const producto = productosRecepcion[index];

        // No puede recibir más de lo pendiente
        const maxCantidad = producto.cantidad_pendiente;
        const cantidadFinal = Math.min(cantidad, maxCantidad);

        const newProductos = [...productosRecepcion];
        newProductos[index].cantidad_a_recibir = cantidadFinal;
        setProductosRecepcion(newProductos);

        // Limpiar error si existe
        if (errors[index]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handlePrecioChange = (index, newPrecio) => {
        const precio = parseFloat(newPrecio);
        if (isNaN(precio) || precio < 0) return;

        const newProductos = [...productosRecepcion];
        newProductos[index].precio_unitario = precio;
        setProductosRecepcion(newProductos);

        // Limpiar error de precio si existe
        if (errors[`precio_${index}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`precio_${index}`];
                return newErrors;
            });
        }
    };

    const handleRecibirTodo = () => {
        const newProductos = productosRecepcion.map(p => ({
            ...p,
            cantidad_a_recibir: p.cantidad_pendiente
        }));
        setProductosRecepcion(newProductos);
    };

    const validateRecepcion = () => {
        const newErrors = {};
        let hayRecepcion = false;

        productosRecepcion.forEach((producto, index) => {
            if (producto.cantidad_a_recibir > producto.cantidad_pendiente) {
                newErrors[index] = 'No puede recibir más de lo pendiente';
            }
            if (producto.cantidad_a_recibir > 0) {
                hayRecepcion = true;
                // Validar que tenga precio si se va a recibir
                if (!producto.precio_unitario || producto.precio_unitario <= 0) {
                    newErrors[`precio_${index}`] = 'Ingrese el precio';
                }
            }
        });

        if (!hayRecepcion) {
            newErrors.general = 'Debe recibir al menos un producto';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirmarRecepcion = () => {
        if (!validateRecepcion()) {
            return;
        }

        const productosParaRecibir = productosRecepcion
            .filter(p => p.cantidad_a_recibir > 0)
            .map(p => ({
                detalle_id: p.detalle_id,
                producto_id: p.producto_id,
                cantidad_recibida: p.cantidad_a_recibir,
                precio_unitario: p.precio_unitario
            }));

        onRecibir(productosParaRecibir, estadoPago);
    };

    const getTotalRecepcion = () => {
        return productosRecepcion.reduce(
            (sum, p) => sum + (p.cantidad_a_recibir * p.precio_unitario),
            0
        );
    };

    const hayPendientes = productosRecepcion.some(p => p.cantidad_pendiente > 0);
    const esRecepcionParcial = productosRecepcion.some(
        p => p.cantidad_a_recibir > 0 && p.cantidad_a_recibir < p.cantidad_pendiente
    );

    return (
        <Modal
            title={`Recepción de Orden - ${orden?.folio}`}
            onClose={onClose}
            size="large"
        >
            <div className="recepcion-modal">
                <div className="recepcion-header">
                    <div className="header-info">
                        <span className="proveedor-label">Proveedor:</span>
                        <span className="proveedor-nombre">{orden?.proveedor_nombre}</span>
                    </div>
                    <Button
                        variant="outline"
                        onClick={handleRecibirTodo}
                        disabled={isLoading || !hayPendientes}
                        size="small"
                    >
                        <Check size={16} />
                        Recibir Todo
                    </Button>
                </div>

                {errors.general && (
                    <div className="error-banner">
                        <AlertTriangle size={16} />
                        {errors.general}
                    </div>
                )}

                <div className="recepcion-table-container">
                    <table className="recepcion-table">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="text-center">Ordenado</th>
                                <th className="text-center">Recibido</th>
                                <th className="text-center">Pendiente</th>
                                <th className="text-center">A Recibir</th>
                                <th className="text-center">Precio Unit.</th>
                                <th className="text-right">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productosRecepcion.map((producto, index) => (
                                <tr
                                    key={index}
                                    className={producto.cantidad_pendiente === 0 ? 'completed' : ''}
                                >
                                    <td>
                                        <div className="producto-info">

                                            <span>{producto.producto_nombre}</span>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {producto.cantidad_ordenada}
                                    </td>
                                    <td className="text-center recibido">
                                        {producto.cantidad_recibida_anterior}
                                    </td>
                                    <td className="text-center pendiente">
                                        {producto.cantidad_pendiente}
                                    </td>
                                    <td className="text-center">
                                        <input
                                            type="number"
                                            value={producto.cantidad_a_recibir}
                                            onChange={(e) => handleCantidadChange(index, e.target.value)}
                                            className={`cantidad-input ${errors[index] ? 'error' : ''}`}
                                            min="0"
                                            max={producto.cantidad_pendiente}
                                            step="1"
                                            disabled={isLoading || producto.cantidad_pendiente === 0}
                                        />
                                        {errors[index] && (
                                            <span className="input-error">{errors[index]}</span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <input
                                            type="number"
                                            value={producto.precio_unitario || ''}
                                            onChange={(e) => handlePrecioChange(index, e.target.value)}
                                            className={`precio-input ${errors[`precio_${index}`] ? 'error' : ''}`}
                                            min="0"
                                            step="0.01"
                                            placeholder="$0.00"
                                            disabled={isLoading || producto.cantidad_pendiente === 0}
                                        />
                                        {errors[`precio_${index}`] && (
                                            <span className="input-error">{errors[`precio_${index}`]}</span>
                                        )}
                                    </td>
                                    <td className="text-right subtotal">
                                        {formatMoney(producto.cantidad_a_recibir * producto.precio_unitario)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="recepcion-totales">
                    <div className="total-row">
                        <span>Total a recibir:</span>
                        <span className="total-valor">{formatMoney(getTotalRecepcion())}</span>
                    </div>
                </div>

                {esRecepcionParcial && (
                    <div className="warning-banner">
                        <AlertTriangle size={16} />
                        <span>Esta es una recepción parcial. La orden quedará como "Recibida Parcialmente".</span>
                    </div>
                )}

                {/* Selector de estado de pago */}
                <div className="estado-pago-section">
                    <h4>Estado de Pago</h4>
                    <div className="estado-pago-options">
                        <label
                            className={`pago-option ${estadoPago === 'pagada' ? 'selected pagada' : ''}`}
                            onClick={() => setEstadoPago('pagada')}
                        >
                            <input
                                type="radio"
                                name="estadoPago"
                                value="pagada"
                                checked={estadoPago === 'pagada'}
                                onChange={() => setEstadoPago('pagada')}
                            />
                            <DollarSign size={18} />
                            <span>Pagada</span>
                        </label>
                        <label
                            className={`pago-option ${estadoPago === 'pendiente' ? 'selected pendiente' : ''}`}
                            onClick={() => setEstadoPago('pendiente')}
                        >
                            <input
                                type="radio"
                                name="estadoPago"
                                value="pendiente"
                                checked={estadoPago === 'pendiente'}
                                onChange={() => setEstadoPago('pendiente')}
                            />
                            <Clock size={18} />
                            <span>Pendiente de Pago</span>
                        </label>
                    </div>
                    {estadoPago === 'pendiente' && (
                        <p className="pago-info">Esta orden aparecerá en "Órdenes por Pagar"</p>
                    )}
                </div>

                <div className="recepcion-actions">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmarRecepcion}
                        loading={isLoading}
                        disabled={isLoading || !hayPendientes}
                    >
                        <Check size={18} />
                        Confirmar Recepción
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RecepcionModal;
